import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  fetchProcurementAggregate,
  fetchAllProcurementContractRows,
  mapSortKeyToApi,
} from '@/lib/api/procurementServer';
import {
  mapAggregateByCountry,
  buildSummaryFromAggregate,
  mergeVendorRawCounts,
  normalizeAggregateCountryKey,
} from '@/lib/procurementAggregateAdapters';
import { shouldCollapseLmsFamily } from '@/lib/procurementCompetitors';

/**
 * Server-side contract intelligence data (Path A).
 * Refetches /aggregate only when filter dimensions change (not on table page).
 * Refetches /contract-table when filters, sort, dir, or page change.
 *
 * Load results are generation-guarded so a stale response (e.g. still carrying
 * decisionWindows after Clear-all) cannot overwrite a newer unfiltered fetch.
 */
export function useProcurementServerIntelligence({
  enabled,
  tenantId,
  categories = [],
  countries = [],
  institutionTypes = [],
  /** [2026-10-17] closed_deals.status — Active / Expired / Retired. */
  statuses = [],
  suppliers = [],
  productNames = [],
  /** [2026-10-13] Contract-table-only filter: narrows the table, never the aggregate (map/KPIs). */
  frameworks = [],
  /** [2026-10-13] Contract-table-only, like frameworks: it never reached the server before. */
  resellers = [],
  sentiments = [],
  decisionWindows = [],
  search = '',
  exitFilter = 'all',
  sortConfig,
  selectedMapCountry = null,
  pageSize = 50,
}) {
  const [aggregate, setAggregate] = useState(null);
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  // A string key, not the array: a default `[]` is a new array every render and would refire load.
  const frameworksKey = JSON.stringify(Array.isArray(frameworks) ? [...frameworks].sort() : []);
  const resellersKey = JSON.stringify(Array.isArray(resellers) ? [...resellers].sort() : []);

  const loadGenRef = useRef(0);
  const prevAggregateKeyRef = useRef('');
  const prevSortKeyRef = useRef('');

  const countriesForQuery = useMemo(() => {
    if (!selectedMapCountry) return countries;
    const base = Array.isArray(countries) ? [...countries] : [];
    const canon = selectedMapCountry;
    if (!base.some((c) => String(c).toLowerCase() === String(canon).toLowerCase())) {
      base.push(canon);
    }
    return base;
  }, [countries, selectedMapCountry]);

  const baseFilters = useMemo(
    () => ({
      categories,
      countries: countriesForQuery,
      institutionTypes,
      statuses,
      suppliers,
      productNames,
      sentiments,
      decisionWindows,
      search: search?.trim() || undefined,
      exitFilter,
      // [2026-10-17] frameworks/resellers are now part of the AGGREGATE key too, not just the
      // table's. procurement_aggregate gained p_frameworks/p_resellers, so the map, KPIs and
      // market share move with them; before this they were contract-table-only and the map sat
      // unchanged while the table collapsed. Parsed from the sorted key strings so the memo only
      // produces a new array when the selection actually changes.
      frameworks: JSON.parse(frameworksKey),
      resellers: JSON.parse(resellersKey),
    }),
    [
      categories,
      countriesForQuery,
      institutionTypes,
      statuses,
      suppliers,
      productNames,
      sentiments,
      decisionWindows,
      search,
      exitFilter,
      frameworksKey,
      resellersKey,
    ],
  );

  const aggregateKey = useMemo(
    () => JSON.stringify({ tenantId, ...baseFilters }),
    [tenantId, baseFilters],
  );

  useEffect(() => {
    if (!enabled) {
      // Invalidate in-flight Path A loads and drop stale filtered aggregates so
      // Clear-all (client → server handoff) cannot leave totalDeals stuck.
      loadGenRef.current += 1;
      prevAggregateKeyRef.current = '';
      prevSortKeyRef.current = '';
      setAggregate(null);
      setTable(null);
      setLoading(false);
      setError(null);
    }
  }, [enabled]);

  const load = useCallback(async () => {
    if (!enabled || !tenantId) return;
    const myGen = ++loadGenRef.current;
    const sortK = `${sortConfig?.key ?? ''}|${sortConfig?.direction ?? ''}`;
    const aggChanged = prevAggregateKeyRef.current !== aggregateKey;

    const tableFilters = {
      ...baseFilters,
      sort: mapSortKeyToApi(sortConfig?.key),
      dir: sortConfig?.direction,
    };

    setLoading(true);
    setError(null);
    try {
      if (aggChanged) {
        const [agg, tbl] = await Promise.all([
          fetchProcurementAggregate(tenantId, baseFilters),
          fetchAllProcurementContractRows(tenantId, tableFilters),
        ]);
        if (myGen !== loadGenRef.current) return;
        setAggregate(agg);
        setTable(tbl);
        prevAggregateKeyRef.current = aggregateKey;
        prevSortKeyRef.current = sortK;
      } else {
        const tbl = await fetchAllProcurementContractRows(tenantId, tableFilters);
        if (myGen !== loadGenRef.current) return;
        setTable(tbl);
        prevSortKeyRef.current = sortK;
      }
    } catch (err) {
      if (myGen !== loadGenRef.current) return;
      setError(err?.message || 'Failed to load contract intelligence');
      setAggregate(null);
      setTable(null);
      prevAggregateKeyRef.current = '';
      prevSortKeyRef.current = '';
    } finally {
      if (myGen === loadGenRef.current) setLoading(false);
    }
  }, [
    enabled,
    tenantId,
    aggregateKey,
    baseFilters,
    frameworksKey,
    resellersKey,
    sortConfig?.key,
    sortConfig?.direction,
  ]);

  useEffect(() => {
    if (enabled) setPage(1);
  }, [
    enabled,
    categories,
    countries,
    institutionTypes,
    statuses,
    suppliers,
    productNames,
    sentiments,
    decisionWindows,
    search,
    exitFilter,
    selectedMapCountry,
    frameworksKey,
    resellersKey,
  ]);

  useEffect(() => {
    if (!enabled) return;
    load();
  }, [enabled, aggregateKey, sortConfig?.key, sortConfig?.direction, load]);

  const byCountryRows = useMemo(
    () => mapAggregateByCountry(aggregate?.by_country),
    [aggregate],
  );

  const byCountryDealCounts = useMemo(() => {
    const o = {};
    for (const row of aggregate?.by_country || []) {
      const c = normalizeAggregateCountryKey(row.country);
      o[c] = (o[c] || 0) + (row.deal_count ?? 0);
    }
    return o;
  }, [aggregate]);

  /** Per-country USD total for map tooltips (Path A aggregate). */
  const byCountryValueUSD = useMemo(() => {
    const o = {};
    for (const row of aggregate?.by_country || []) {
      const c = normalizeAggregateCountryKey(row.country);
      const v = row.total_value_usd ?? row.total_value_incl_est_usd;
      const num = v != null && !Number.isNaN(Number(v)) ? Number(v) : 0;
      o[c] = (o[c] || 0) + num;
    }
    return o;
  }, [aggregate]);

  const mergedVendors = useMemo(
    () => mergeVendorRawCounts(aggregate?.by_vendor_raw, tenantId, {
      collapseLmsFamily: shouldCollapseLmsFamily(categories),
    }),
    [aggregate, tenantId, categories],
  );

  const summary = useMemo(
    () => buildSummaryFromAggregate(aggregate, mergedVendors),
    [aggregate, mergedVendors],
  );

  const rows = table?.rows || [];
  const totalAccounts = table?.total_accounts ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalAccounts / pageSize));

  return {
    aggregate,
    table,
    rows,
    loading,
    error,
    page,
    setPage,
    totalAccounts,
    totalPages,
    totalDeals: aggregate?.totals?.deal_count ?? table?.total_deals ?? 0,
    byCountryRows,
    byCountryDealCounts,
    byCountryValueUSD,
    mergedVendors,
    summary,
    reload: load,
  };
}
